import OrderPendingTimer from './OrderPendingTimer';
import React, { useId, useRef, useState } from 'react';
import { updateOrderLifecycleAPI } from '../../auth/paymentsApi';
import { elakayAutoText as t } from '../dataElakay';
import { parseAmountToCents, formatCents } from './paymentMethodsLogic';
import './OrderFinancialActions.css';

const OrderFinancialActions = ({ order, line, buyer = false, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [amount, setAmount] = useState('');
  const refundKey = useRef({ amount: null, key: '' });
  const inFlight = useRef(false);
  const amountId = useId();
  const refundedCents = Number(line?.payment?.refundedCents) || 0;
  const remainingCents = Math.max(0, (Number(line?.payment?.amountCents) || 0) - refundedCents);
  const enteredCents = parseAmountToCents(amount);
  const validAmount = Number.isSafeInteger(enteredCents) && enteredCents > 0 && enteredCents <= remainingCents;
  const lifecycle = line?.payment?.lifecycle || {};
  const statusLabel = value => t(`h_pay_state_${String(value).toLowerCase().replace(/\s+/g, '_')}`, value);
  const act = async body => {
    if (inFlight.current) return;
    let headers;
    if (body.action === 'refund') {
      if (!Number.isSafeInteger(body.amountCents) || body.amountCents <= 0 || body.amountCents > remainingCents) return;
      if (!window.confirm(t('h_pay_refund_confirm', 'Submit this refund to the original payment provider?'))) return;
      if (refundKey.current.amount !== body.amountCents) refundKey.current = { amount: body.amountCents, key: window.crypto?.randomUUID?.() || `refund_${Date.now()}_${Math.random().toString(36).slice(2)}` };
      headers = { 'Idempotency-Key': refundKey.current.key };
    }
    inFlight.current = true;
    setBusy(true); setError('');
    try { await updateOrderLifecycleAPI(order?._id || order?.orderId, line?._id || line?.orderItemId, body, { headers }); if (body.action === 'refund') setAmount(''); await onChanged?.(); }
    catch (err) { setError(err.message); }
    finally { inFlight.current = false; setBusy(false); }
  };
  const button = (label, body) => <button type="button" disabled={busy} className="elk-btn elk-btn--ghost" onClick={() => act(body)}>{statusLabel(label)}</button>;
  if (!line?.payment) return null;
  const showRefund = !buyer && line.payment.status === 'captured' && remainingCents > 0;
  const hasLifecycle = lifecycle.cancelStatus || lifecycle.returnStatus || lifecycle.refundStatus || lifecycle.externalRefundReview;
  // The buyer's payment method and its pending steps live in OrderPaymentMethod.
  if (!showRefund && !hasLifecycle && !error && line.payment.status !== 'capture_pending' && !line.orderManagement?.autoCancelAt) return null;
  return <div className="ofa-panel" aria-busy={busy}>
    <OrderPendingTimer line={line} />
    {error && <p role="alert">{error}</p>}
    {[lifecycle.cancelStatus, lifecycle.returnStatus, lifecycle.refundStatus, lifecycle.externalRefundReview ? 'Refund Pending' : null].filter(Boolean).map(value => <p key={value} role="status">{statusLabel(value)}</p>)}
    {line.payment.status === 'capture_pending' && <p role="status">{t('h_pay_pending', 'Payment is awaiting provider confirmation. Check your order before retrying.')}</p>}
    {!buyer && lifecycle.cancelStatus === 'Cancel Requested' && <>{button('Cancelled', { action: 'approve_cancel' })}{button('Cancellation Rejected', { action: 'reject_cancel' })}</>}
    {!buyer && lifecycle.returnStatus === 'Return Requested' && <>{button('Return Approved', { status: 'Return Approved' })}{button('Return Rejected', { status: 'Return Rejected' })}</>}
    {buyer && lifecycle.returnStatus === 'Return Approved' && button('Return In Transit', { status: 'Return In Transit' })}
    {!buyer && (!line.dropshippingIn || line.dropshippingIn.fulfillmentOwner === 'SELLER') && lifecycle.returnStatus === 'Return In Transit' && button('Return Received', { status: 'Return Received' })}
    {showRefund && <div className="ofa-refund">
      <h5 className="ofa-title">{t('h_pay_refund_title', 'Refund payment')}</h5>
      <dl className="ofa-summary">
        <div><dt>{t('h_pay_refund_available', 'Available to refund')}</dt><dd>{formatCents(remainingCents, line.payment.currency)}</dd></div>
        <div><dt>{t('h_pay_refund_recorded', 'Confirmed refunded amount')}</dt><dd>{formatCents(refundedCents, line.payment.currency)}</dd></div>
      </dl>
      <div className="ofa-controls">
        <div className="ofa-field">
          <label htmlFor={amountId}>{t('h_pay_refund_amount', 'Refund amount')} ({String(line.payment.currency || 'usd').toUpperCase()})</label>
          <input id={amountId} type="text" inputMode="decimal" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} disabled={busy} aria-invalid={amount !== '' && !validAmount} aria-describedby={amount !== '' && !validAmount ? `${amountId}-error` : undefined} />
        </div>
        <div className="ofa-buttons">
          <button className="ofa-button ofa-button--primary" type="button" disabled={busy || !validAmount} onClick={() => act({ action: 'refund', amountCents: enteredCents })}>{t('h_pay_partial_refund', 'Partial refund')}</button>
          <button className="ofa-button ofa-button--outline" type="button" disabled={busy} onClick={() => act({ action: 'refund', amountCents: remainingCents })}>{t('h_pay_full_refund', 'Full refund')}</button>
        </div>
      </div>
      {amount !== '' && !validAmount && <p id={`${amountId}-error`} className="ofa-error">{t('h_pay_refund_amount_limit', 'Enter a positive amount within the available balance.')}</p>}
    </div>}
  </div>;
};
export default OrderFinancialActions;
